import { Suspense, useState } from 'react';
import { hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReakitProvider } from 'reakit';

import { I18nProvider } from '@cx-modules/i18n-provider';
import { DefaultLoadingFallback } from '@cx-modules/react-utils';

import { loadDataForServer } from './loadData';
import { TextBlock } from './TextBlock';
import type { TextBlockIndexProps } from './types';

const createQueryClient = (nocache?: boolean) =>
  new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: nocache ? 0 : undefined,
        refetchOnMount: nocache ? true : false,
        staleTime: nocache ? 0 : Infinity,
      },
    },
  });

function TextBlockWithQuery({ dehydratedState, nocache, ...props }: TextBlockIndexProps) {
  const [queryClient] = useState(() => {
    const client = createQueryClient(nocache);
    hydrate(client, dehydratedState);
    return client;
  });

  return (
    <ReakitProvider>
      <QueryClientProvider client={queryClient}>
        <I18nProvider i18nInstance={props.i18n}>
          <Suspense fallback={<DefaultLoadingFallback />}>
            <TextBlock {...props} />
          </Suspense>
        </I18nProvider>
      </QueryClientProvider>
    </ReakitProvider>
  );
}

// The props type is part of the public API — every consumer wraps this component and types its
// own props with it. Re-exported explicitly: the dist `.d.ts` dts-bundle-generator used to emit
// hoisted it automatically, and since NGF-6661 this file IS the package's type surface.
export type { TextBlockIndexProps } from './types';

export { TextBlockWithQuery, loadDataForServer };
